type Pos = [number, number] | number[];

/** Where `p` meets `route`: metres along it, metres away from it and the closest point on it. */
interface RouteHit {
  along: number;
  distance: number;
  point: [number, number];
}

/**
 * The point of `route` closest to `p`. Plane projection scaled at the route's latitude: precise
 * enough to order and check stops within a city.
 */
export function nearestOnRoute(route: Pos[], p: Pos): RouteHit {
  if (route.length < 2) return { along: 0, distance: 0, point: [p[0] as number, p[1] as number] };
  const k = Math.cos(((route[0]![1] as number) * Math.PI) / 180);
  const xy = (c: Pos) => [(c[0] as number) * k * 111_320, (c[1] as number) * 110_540];
  const [px, py] = xy(p) as [number, number];
  let travelled = 0;
  let best = { d: Infinity, at: 0, cx: 0, cy: 0 };
  for (let i = 0; i < route.length - 1; i++) {
    const [ax, ay] = xy(route[i]!) as [number, number];
    const [bx, by] = xy(route[i + 1]!) as [number, number];
    const [dx, dy] = [bx - ax, by - ay];
    const len2 = dx * dx + dy * dy;
    const t = len2 ? Math.max(0, Math.min(1, ((px - ax) * dx + (py - ay) * dy) / len2)) : 0;
    const [cx, cy] = [ax + t * dx, ay + t * dy];
    const d = Math.hypot(px - cx, py - cy);
    if (d < best.d) best = { d, at: travelled + t * Math.sqrt(len2), cx, cy };
    travelled += Math.sqrt(len2);
  }
  return { along: best.at, distance: best.d, point: [best.cx / (k * 111_320), best.cy / 110_540] };
}

/** Distance (in metres, approximately) from the start of `route` to the point on it closest to `p`. */
export function positionAlong(route: Pos[], p: Pos): number {
  return nearestOnRoute(route, p).along;
}

/**
 * Where a new stop belongs in `ordered` (stop ids in travel order): before the first stop that lies
 * further along the route. Appends when the route is missing.
 */
export function insertionIndex(
  route: Pos[] | undefined,
  ordered: string[],
  positionOf: (id: string) => Pos | undefined,
  p: Pos,
): number {
  if (!route || route.length < 2) return ordered.length;
  const at = positionAlong(route, p);
  const i = ordered.findIndex((id) => {
    const q = positionOf(id);
    return q !== undefined && positionAlong(route, q) > at;
  });
  return i === -1 ? ordered.length : i;
}
